import { useEffect, useState } from "react";

import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { listMyIntelSubmission, SubmissionSummary } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";

import {
  ReportStatus,
  reportColourTypes,
  statusTypes,
} from "@/types/typesReportV2";

import styles from "./CISOReportSelect.module.scss";

const CISODraftsList = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();

  const [drafts, setDrafts] = useState<SubmissionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDrafts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await listMyIntelSubmission();

        setDrafts(response.data?.submissions ?? []);
      } catch (err) {
        console.error("Error fetching CISO drafts:", err);
        setError("Failed to load drafts");
        toast.error("Failed to load drafts");
      } finally {
        setLoading(false);
      }
    };

    fetchDrafts();
  }, []);

  const handleContinue = (submissionId: string) => {
    navigate(`/rubiintel/${orgId}/report/${submissionId}/edit`);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className={styles.selectContainer}>
      <div className={styles.headerContainer}>
        <img
          src="/service-logos/CISO-Logo.png"
          alt="CISO Logo"
          className={styles.cisoLogo}
        />
        <Typography variant="h4" className={styles.title}>
          My Drafts & Updates
        </Typography>
      </div>

      {loading && (
        <Grid container justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={50} thickness={2} />
        </Grid>
      )}

      {!loading && error && (
        <Typography color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && drafts.length === 0 && (
        <Typography variant="body1" sx={{ py: 2 }}>
          No drafts or pending updates. Start a submission from CISO to save a
          draft.
        </Typography>
      )}

      {!loading && !error && drafts.length > 0 && (
        <Grid container spacing={2} className={styles.reportCardGrid}>
          {drafts.map((draft) => (
            <Grid size={{ xs: 12 }} key={draft.id}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <div>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {draft.survey_name ?? "Submission"}
                    <Chip
                      label={
                        statusTypes[draft.status as ReportStatus] ??
                        draft.status
                      }
                      color={
                        reportColourTypes[draft.status as ReportStatus] ??
                        "default"
                      }
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Updated {formatDate(draft.time_updated)}
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  onClick={() => handleContinue(draft.id)}
                >
                  {draft.status === "modification_requested"
                    ? "Update"
                    : "Continue"}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default CISODraftsList;
